import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { flujoCajaApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import { RefreshCw, Zap, ChevronDown, ChevronRight, Calendar, Check, MoveRight, Trash2, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, Printer, FileSpreadsheet, ExternalLink, Stamp, ShieldCheck, Landmark } from 'lucide-react';
import { exportToCSV, triggerPrint } from '../../lib/printUtils';
import { useAuth } from '../../lib/auth';
import { ConciliaBadge } from './cxp/ConciliaBadge';
import { VBForzadoDialog } from './cxp/VBForzadoDialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu';
import { DteDocsActions } from '../dte/DteDocsActions'; // Iter 235.d
import { AlegraStatusBadge } from '../dte/AlegraStatusBadge'; // Iter 235.e
import { StickyHScroll } from '../ui/sticky-hscroll'; // Iter 235.g
import { useRegisterCmdKCommands } from '../../contexts/CmdKContext'; // Iter 235.h
import RowFormaPagoSelect from './RowFormaPagoSelect';
// Iter 236.m.14 (Carlos): subcomponentes extraídos a archivos propios para
// reducir tamaño de CxPPanel.jsx (era 1737 → ahora ~1200 líneas). Cobertura
// E2E garantizada por specs/CxPPanel.spec.ts.
import { BatchPagarDialog } from './cxp/BatchPagarDialog';
import { BatchMoverDialog } from './cxp/BatchMoverDialog';
import { BatchNominaEspecialDialog } from './cxp/BatchNominaEspecialDialog';
import { MoverFacturaDialog } from './cxp/MoverFacturaDialog';
import { CleanupResiduosDialog } from './cxp/CleanupResiduosDialog';
import { ConfirmarPagoFacturaDialog } from './cxp/ConfirmarPagoFacturaDialog';
import { ConfirmarPagoNominaDialog } from './cxp/ConfirmarPagoNominaDialog';
import { BatchActionsBar } from './cxp/BatchActionsBar';
import { SyncErrorsBanner } from './cxp/SyncErrorsBanner';
import { DuplicadosAlegraBanner } from './cxp/DuplicadosAlegraBanner';
import { TefResultDialog } from './cxp/TefResultDialog';
import { TefContabConfirmDialog } from './cxp/TefContabConfirmDialog';

const fmt = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;
const fmtFecha = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

// YYYY-MM-DD de hoy en zona local Chile (sin horas)
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Días vencidos respecto a hoy (positivo = atrasada). Negativo o 0 = al día.
const diasVencida = (fechaIso) => {
  if (!fechaIso) return 0;
  const t = todayIso();
  if (fechaIso >= t) return 0;
  const a = new Date(fechaIso + 'T12:00:00');
  const b = new Date(t + 'T12:00:00');
  return Math.round((b - a) / 86400000);
};

// Clasifica días atrasados en bucket visual (Carlos: según acuerdo comercial Alegra).
//   0       → al día (muted)
//   1-15    → aviso (amber)
//   16-30   → crítico (orange)
//   >30     → EXCEDIDO (red + bold)
const diasAtrasoBucket = (dias) => {
  if (!dias || dias <= 0) return { color: 'text-muted-foreground', label: '—', bold: false };
  if (dias <= 15) return { color: 'text-amber-700 bg-amber-100 dark:bg-amber-950/40', label: `${dias}d`, bold: false };
  if (dias <= 30) return { color: 'text-orange-700 bg-orange-100 dark:bg-orange-950/40', label: `${dias}d`, bold: false };
  return { color: 'text-red-700 bg-red-100 dark:bg-red-950/50 font-bold', label: `${dias}d`, bold: true };
};

// localStorage key for column sort preference (persisted per user)
const SORT_STORAGE_KEY = 'cxp-panel-sort';
const DEFAULT_SORT = { key: 'nombre_proveedor', direction: 'asc' };

// Comparador genérico tolerante a nulls / strings con locale es-CL
const cmp = (a, b, dir) => {
  const mult = dir === 'desc' ? -1 : 1;
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * mult;
  return String(a).localeCompare(String(b), 'es-CL', { sensitivity: 'base', numeric: true }) * mult;
};

// Ordena facturas según sortConfig. Claves soportadas: nombre_proveedor, folio,
// fecha_vencimiento_alegra, fecha_vencimiento (ERP), monto, dias_atraso, estado.
const sortFacturas = (facturas, sortConfig) => {
  if (!Array.isArray(facturas)) return [];
  const { key, direction } = sortConfig || DEFAULT_SORT;
  const arr = [...facturas];
  arr.sort((fa, fb) => {
    let va, vb;
    switch (key) {
      case 'folio':
        va = fa.folio; vb = fb.folio; break;
      case 'fecha_vencimiento_alegra':
        va = fa.fecha_vencimiento_alegra || fa.fecha_vencimiento;
        vb = fb.fecha_vencimiento_alegra || fb.fecha_vencimiento;
        break;
      case 'fecha_vencimiento':
        va = fa.fecha_vencimiento; vb = fb.fecha_vencimiento; break;
      case 'monto':
        va = Number(fa.monto || 0); vb = Number(fb.monto || 0); break;
      case 'dias_atraso': {
        const da = fa.estado === 'pendiente' ? diasVencida(fa.fecha_vencimiento_alegra || fa.fecha_vencimiento) : 0;
        const db = fb.estado === 'pendiente' ? diasVencida(fb.fecha_vencimiento_alegra || fb.fecha_vencimiento) : 0;
        va = da; vb = db; break;
      }
      case 'estado':
        va = fa.estado; vb = fb.estado; break;
      case 'nombre_proveedor':
      default:
        va = fa.nombre_proveedor; vb = fb.nombre_proveedor;
    }
    return cmp(va, vb, direction);
  });
  return arr;
};

export default function CxPPanel({ initialFilterDate = null, initialFilterOrden = null }) {
  const { user } = useAuth();
  const canForceVB = ['admin', 'administrador', 'gerencia', 'contabilidad', 'finanzas']
    .includes(String(user?.rol || user?.role || '').toLowerCase());
  const [data, setData] = useState({ nominas: [], sin_nomina: [], total_pendiente: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [moverDialog, setMoverDialog] = useState(null); // {factura, nominas}
  const [vbDialog, setVbDialog] = useState(null); // {factura}
  const [moverFechaCustom, setMoverFechaCustom] = useState('');
  const [selected, setSelected] = useState(() => new Set()); // Set<facturaId>
  const [batchMoverDialog, setBatchMoverDialog] = useState(null); // {nominas}
  const [batchMoverFecha, setBatchMoverFecha] = useState('');
  const [batchPagarDialog, setBatchPagarDialog] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  // Iter 234.c (Carlos): por defecto ocultar facturas pagadas. Toggle por usuario.
  const [mostrarPagadas, setMostrarPagadas] = useState(() => {
    try { return localStorage.getItem('cxp-mostrar-pagadas') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('cxp-mostrar-pagadas', mostrarPagadas ? '1' : '0'); } catch { /* ignore */ }
  }, [mostrarPagadas]);

  // Iter 236.l (Carlos): buscador local en nómina por proveedor / folio / RUT
  const [searchTerm, setSearchTerm] = useState('');

  // Sort config persistido en localStorage (Punto 3: nóminas ordenables)
  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const raw = localStorage.getItem(SORT_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return DEFAULT_SORT;
  });
  useEffect(() => {
    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sortConfig));
    } catch { /* ignore */ }
  }, [sortConfig]);

  // Click en un header: asc → desc → default (nombre_proveedor asc)
  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return DEFAULT_SORT;
    });
  };

  // Ícono del header según estado actual
  const SortIcon = ({ active, direction }) => {
    if (!active) return <ArrowUpDown className="inline h-3 w-3 ml-0.5 opacity-40" />;
    return direction === 'desc'
      ? <ArrowDown className="inline h-3 w-3 ml-0.5 text-blue-600" />
      : <ArrowUp className="inline h-3 w-3 ml-0.5 text-blue-600" />;
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllNomina = (n, allSelected) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const ids = (n.facturas || []).filter((f) => f.estado === 'pendiente').map((f) => f.id);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelected = () => setSelected(new Set());

  // Acciones batch (NO refrescan entre llamadas, solo al final)
  // Iter 234.c (Carlos) — Pagar selección creando UN egreso por proveedor en Alegra.
  const ejecutarBatchPagar = async () => {
    if (selected.size === 0) return;
    setBatchBusy(true);
    try {
      const res = await api.post('/flujo-caja/cxp/pagar-agrupado-por-proveedor', {
        factura_ids: Array.from(selected),
      });
      const d = res.data || {};
      const egresos = d.egresos_creados || 0;
      const pagadas = d.facturas_pagadas || 0;
      const errores = (d.errores || []).length;
      const bloqueadas = (d.bloqueadas || []).length;
      toast({
        title: `${egresos} egreso(s) Alegra · ${pagadas} factura(s) pagadas`,
        description: [
          errores > 0 ? `❌ ${errores} con error de sync` : null,
          bloqueadas > 0 ? `🔒 ${bloqueadas} bloqueadas por falta VB` : null,
          d.proveedores?.length ? `Proveedores: ${d.proveedores.map(p => p.proveedor_nombre).join(', ')}` : null,
        ].filter(Boolean).join(' · '),
        variant: errores > 0 ? 'destructive' : 'default',
      });
    } catch (e) {
      toast({
        title: 'Pago agrupado falló',
        description: e?.response?.data?.detail || e?.message || 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setBatchBusy(false);
      setBatchPagarDialog(false);
      clearSelected();
      load();
      loadErrorsBanner();
    }
  };

  const ejecutarBatchMover = async (nomina_id_destino, fecha_pago) => {
    if (selected.size === 0) return;
    setBatchBusy(true);
    let ok = 0, fail = 0;
    for (const id of selected) {
      try {
        // Iter 236.n (Carlos): el backend espera `nomina_id` / `fecha_pago_nueva`.
        // (Acepta también los aliases viejos, pero usamos los nombres canónicos.)
        const body = nomina_id_destino
          ? { nomina_id: nomina_id_destino }
          : { fecha_pago_nueva: fecha_pago };
        await api.patch(`/flujo-caja/cxp/facturas/${id}/mover`, body);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setBatchBusy(false);
    setBatchMoverDialog(null);
    setBatchMoverFecha('');
    clearSelected();
    toast({
      title: `Movidas: ${ok} OK · ${fail} fallidas`,
      variant: fail > 0 ? 'destructive' : 'default',
    });
    load();
  };

  // Iter 236.n (Carlos): crear una nómina especial/extra con N facturas seleccionadas
  // (extraídas desde una o más nóminas existentes). Permite armar varias nóminas
  // especiales sin tocar el resto del flujo.
  const [nominaEspecialDialog, setNominaEspecialDialog] = useState(null); // {abierto}
  const ejecutarCrearNominaEspecial = async ({ nombre, fecha_pago, observaciones }) => {
    if (selected.size === 0) return;
    setBatchBusy(true);
    try {
      const body = {
        nombre,
        fecha_pago: fecha_pago || null,
        observaciones: observaciones || '',
        factura_ids: Array.from(selected),
      };
      const res = await api.post('/flujo-caja/cxp/nominas/especial', body);
      toast({
        title: `Nómina "${nombre}" creada`,
        description: `${res.data?.movidas || 0} factura(s) movidas`,
      });
      setNominaEspecialDialog(null);
      clearSelected();
      load();
    } catch (err) {
      toast({
        title: 'Error al crear nómina especial',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBatchBusy(false);
    }
  };
  const [pagadasConError, setPagadasConError] = useState([]);

  // Iter 268 (Carlos): generación de archivo TEF Banco de Chile con las facturas
  // SELECCIONADAS de la nómina (checkbox). El backend arma el/los .txt de ancho fijo.
  const [tefBusyId, setTefBusyId] = useState(null);
  const [tefResult, setTefResult] = useState(null);
  const [tefContab, setTefContab] = useState(null); // {facturaIds, nombre, count} | null
  const [tefContabBusy, setTefContabBusy] = useState(false);

  const descargarTxt = (filename, contenidoB64) => {
    // Decodifica base64 → bytes exactos (el contenido va en latin-1, sin re-encode UTF-8).
    const bin = atob(contenidoB64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const generarTef = async (nomina) => {
    let ids = (nomina.facturas || []).filter((f) => selected.has(f.id)).map((f) => f.id);
    if (ids.length === 0 && nomina.estado === 'pagada') {
      // Nómina ya pagada: recuperar el archivo TEF con todas sus facturas
      ids = (nomina.facturas || []).map((f) => f.id);
    }
    if (ids.length === 0) {
      toast({
        title: 'Selecciona al menos una factura',
        description: 'Marca con el checkbox las facturas de esta nómina que quieres incluir en el archivo TEF.',
        variant: 'destructive',
      });
      return;
    }
    setTefBusyId(nomina.id);
    try {
      const res = await api.post(`/tef/nominas/${nomina.id}/generar`, { factura_ids: ids });
      // Previsualización: NO se descarga aún. El usuario confirma con "Descargar".
      setTefResult({ ...(res.data || {}), nomina });
    } catch (e) {
      toast({
        title: 'Error al generar TEF',
        description: e?.response?.data?.detail || e?.message || 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setTefBusyId(null);
    }
  };

  // "Descargar" en la previsualización: baja el/los .txt, cierra el diálogo y
  // ofrece registrar el pago de los documentos INCLUIDOS en Contabilidad (Alegra).
  const descargarTefResult = () => {
    const r = tefResult;
    if (!r) return;
    (r.archivos || []).forEach((a) => descargarTxt(a.filename, a.contenido_b64));
    const incluidasIds = (r.incluidas || []).map((i) => i.id).filter(Boolean);
    setTefResult(null);
    if (incluidasIds.length > 0) {
      setTefContab({
        facturaIds: incluidasIds,
        nombre: r.nomina?.nombre || r.nombre || 'Nómina',
        count: incluidasIds.length,
        incluidas: (r.incluidas || []).filter((i) => i.id),
      });
    }
  };

  const ejecutarTefContabilizar = async () => {
    if (!tefContab) return;
    setTefContabBusy(true);
    try {
      const res = await api.post('/flujo-caja/cxp/pagar-agrupado-por-proveedor', {
        factura_ids: tefContab.facturaIds,
      });
      const d = res.data || {};
      const egresos = d.egresos_creados || 0;
      const pagadas = d.facturas_pagadas || 0;
      const errores = (d.errores || []).length;
      const bloqueadas = (d.bloqueadas || []).length;
      toast({
        title: `${egresos} egreso(s) Alegra · ${pagadas} factura(s) pagadas`,
        description: [
          errores > 0 ? `❌ ${errores} con error de sync` : null,
          bloqueadas > 0 ? `🔒 ${bloqueadas} bloqueadas por falta VB` : null,
        ].filter(Boolean).join(' · ') || 'Pago registrado en Contabilidad',
        variant: errores > 0 ? 'destructive' : 'default',
      });
    } catch (e) {
      toast({
        title: 'No se pudo registrar el pago',
        description: e?.response?.data?.detail || e?.message || 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setTefContabBusy(false);
      setTefContab(null);
      clearSelected();
      load();
      loadErrorsBanner();
    }
  };

  const [retryingAll, setRetryingAll] = useState(false);
  // Iter 237: detector de facturas duplicadas en Alegra
  const [duplicados, setDuplicados] = useState({ total_grupos: 0, total_riesgo_alto: 0, grupos: [] });
  const [duplicadosLoading, setDuplicadosLoading] = useState(false);
  const [duplicadosOpen, setDuplicadosOpen] = useState(false);

  const token = () => null;  // unused, api client handles auth
  const headers = () => ({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Iter 343 (Renzo): con "Mostrar pagadas" se incluyen nóminas pagadas de los últimos 60 días
      // (permite re-generar el archivo TEF del banco de una nómina ya enviada a pago).
      const url = mostrarPagadas
        ? `/flujo-caja/cxp/nominas?incluir_pagadas=true&pagadas_desde=${new Date(Date.now() - 60 * 86400000).toISOString().slice(0, 10)}`
        : '/flujo-caja/cxp/nominas?incluir_pagadas=false';
      const res = await api.get(url);
      setData(res.data);
    } catch (err) {
      toast({ title: 'Error al cargar CxP', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [mostrarPagadas]);

  const loadErrorsBanner = useCallback(async () => {
    try {
      const res = await flujoCajaApi.listFacturasCxPConErrorAlegra();
      setPagadasConError(res.data.items || []);
    } catch {
      setPagadasConError([]);
    }
  }, []);

  const loadDuplicados = useCallback(async (verificar = true) => {
    setDuplicadosLoading(true);
    try {
      const res = await flujoCajaApi.detectarDuplicadosAlegra(verificar);
      setDuplicados(res.data || { total_grupos: 0, grupos: [] });
    } catch {
      setDuplicados({ total_grupos: 0, total_riesgo_alto: 0, grupos: [] });
    } finally {
      setDuplicadosLoading(false);
    }
  }, []);

  const reintentarAlegraFactura = async (f) => {
    try {
      await flujoCajaApi.reintentarAlegraCxP(f.id);
      toast({ title: `Pago sincronizado con Alegra · ${f.folio || f.id}` });
      loadErrorsBanner();
      load();
    } catch (err) {
      toast({
        title: 'No se pudo sincronizar',
        description: err?.response?.data?.detail || 'Error Alegra',
        variant: 'destructive',
      });
    }
  };

  const reintentarTodosErrores = async () => {
    if (pagadasConError.length === 0) return;
    setRetryingAll(true);
    let ok = 0, fail = 0;
    for (const f of pagadasConError) {
      try {
        await flujoCajaApi.reintentarAlegraCxP(f.id);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setRetryingAll(false);
    toast({
      title: `Reintentos: ${ok} OK, ${fail} fallidos`,
      description: fail > 0 ? 'Revisá credenciales Alegra si sigue fallando' : 'Todos los pagos pusheados',
      variant: fail > 0 ? 'destructive' : 'default',
    });
    loadErrorsBanner();
    load();
  };

  // Iter 236.m.9 (Carlos): auto-arregla los falsos positivos (facturas que
  // YA están pagadas en Alegra pero quedaron con error_sync histórico).
  const [autoFixing, setAutoFixing] = useState(false);
  const autoFixSyncErrors = async () => {
    if (pagadasConError.length === 0) return;
    setAutoFixing(true);
    try {
      const res = await api.post('/flujo-caja/cxp/auto-fix-sync-errors');
      const { fixed = 0, fixed_via_duplicada = 0, kept_real = 0, total = 0 } = res.data || {};
      const limpiadas = fixed + fixed_via_duplicada;
      toast({
        title: `Auto-arreglo completado`,
        description: `${limpiadas} limpiadas (${fixed_via_duplicada} pagadas en copia duplicada) · ${kept_real} pendientes reales · total ${total}`,
        variant: kept_real > 0 ? 'default' : 'default',
      });
    } catch (e) {
      toast({
        title: 'Auto-arreglo falló',
        description: e?.response?.data?.detail || e?.message || 'Error',
        variant: 'destructive',
      });
    } finally {
      setAutoFixing(false);
      loadErrorsBanner();
      load();
    }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadErrorsBanner(); }, [loadErrorsBanner, data]);
  // Iter 237: cargar detector de duplicados una vez al montar (verifica contra Alegra).
  useEffect(() => { loadDuplicados(true); }, [loadDuplicados]);

  // Auto-expandir la nómina cuyo fecha_pago coincide con `initialFilterDate`
  // (drill-down desde el grid: click en celda día X → abre la nómina de ese día).
  useEffect(() => {
    if (!initialFilterDate || !data.nominas.length) return;
    const target = data.nominas.find(n => n.fecha_pago === initialFilterDate);
    if (target) {
      setExpanded(p => ({ ...p, [target.id]: true }));
      // scroll-into-view después de un tick
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="nomina-${target.id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [initialFilterDate, data.nominas]);

  // Próximos 4 viernes (helper para el dialog de mover)
  const proximosViernes = (() => {
    const out = [];
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    let d = new Date(today);
    // Avanza al siguiente viernes (≥ today+1 day)
    const dow = d.getDay(); // 0=dom..6=sab; 5=vie
    let daysToFri = (5 - dow + 7) % 7;
    if (daysToFri === 0) daysToFri = 7;  // si hoy es viernes, próximo viernes (no hoy)
    d.setDate(d.getDate() + daysToFri);
    for (let i = 0; i < 4; i++) {
      const iso = d.toISOString().slice(0, 10);
      const labelDate = d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
      const deltaDays = Math.round((d - today) / 86400000);
      out.push({ iso, label: labelDate, deltaDays });
      d.setDate(d.getDate() + 7);
    }
    return out;
  })();

  const syncAlegra = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/flujo-caja/cxp/sync-alegra', {});
      // Iter 236.g/h (Carlos): además del sync normal (open),
      // dispara el sync inverso (cierres + parciales) en background y hace polling.
      let cerradas = 0;
      let parciales = 0;
      try {
        await api.post('/contabilidad/sync-pagos-inverso-alegra', {});
        // Polling al endpoint /status hasta que el campo "running" sea false
        // (timeout duro a 6 minutos para no quedarse colgado)
        const deadline = Date.now() + 6 * 60 * 1000;
        let done = false;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 5000));
          try {
            const s = await api.get('/contabilidad/sync-pagos-inverso-alegra/status');
            if (s?.data && s.data.running === false) {
              cerradas = s.data.marcadas_pagadas || 0;
              parciales = s.data.marcadas_parciales || 0;
              done = true;
              break;
            }
          } catch (e) { /* sigue intentando */ }
        }
        if (!done) {
          toast({ title: 'Sync Alegra (inverso) en curso', description: 'Sigue corriendo en background; revisá CxP en unos minutos.' });
        }
      } catch (e) { /* sync inverso opcional, no romper sync principal */ }
      toast({
        title: 'Sincronizado con Alegra',
        description: `${res.data.nuevas} nuevas · ${res.data.actualizadas} actualizadas · ${cerradas} cerradas · ${parciales} parciales`,
      });
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const reconciliarPagadas = async () => {
    setReconciling(true);
    try {
      await api.post('/contabilidad/reconciliar-pagadas-alegra', {});
      // Polling al /status hasta running=false (timeout duro 20 min — revisa cientos de bills)
      const deadline = Date.now() + 20 * 60 * 1000;
      let done = false;
      let result = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const s = await api.get('/contabilidad/reconciliar-pagadas-alegra/status');
          if (s?.data && s.data.running === false && s.data.ts) {
            result = s.data;
            done = true;
            break;
          }
        } catch (e) { /* sigue intentando */ }
      }
      if (done && result) {
        const rev = result.revertidas_total || 0;
        toast({
          title: rev > 0 ? `${rev} factura(s) revertidas a pendiente` : 'Sin cambios — todo cuadra con Alegra',
          description: `Verificadas ${result.verificadas} · reabiertas ${result.reabiertas} · eliminadas ${result.eliminadas_sin_pago} · pagadas en duplicada ${result.pagada_en_duplicada}`,
        });
        await load();
      } else {
        toast({ title: 'Reconciliación en curso', description: 'Sigue corriendo en background; revisá CxP en unos minutos.' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setReconciling(false);
    }
  };

  const autoGenerar = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/flujo-caja/cxp/auto-generar-nominas', {});
      toast({ title: res.data.message });
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const cambiarFecha = async (nomina) => {
    const nueva = window.prompt(`Nueva fecha de pago para "${nomina.nombre}" (YYYY-MM-DD):`, nomina.fecha_pago || '');
    if (!nueva) return;
    try {
      await api.patch(`/flujo-caja/cxp/nominas/${nomina.id}/fecha`, { fecha_pago: nueva });
      toast({ title: 'Fecha actualizada' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const pagarNomina = (nomina) => setConfirmPayNomina({ nomina });

  // Iter 404 (Renzo): reintentar la contabilización en Alegra de las facturas pagadas sin egreso.
  const [contabBusyId, setContabBusyId] = useState(null);
  const reintentarContabilizacion = async (nomina) => {
    setContabBusyId(nomina.id);
    try {
      const res = await api.post(`/flujo-caja/cxp/nominas/${nomina.id}/reintentar-contabilizacion`);
      const d = res.data || {};
      toast({
        title: d.integridad_ok ? '✅ Contabilización completa' : `Aún faltan ${d.num_sin_contabilizar} por contabilizar`,
        description: `${d.contabilizadas_ok || 0} egreso(s) creados en Alegra · ${d.num_contabilizadas}/${d.num_pagadas} facturas con egreso`,
        variant: d.integridad_ok ? 'default' : 'destructive',
      });
      load();
    } catch (e) {
      toast({ title: 'Error al reintentar contabilización', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setContabBusyId(null);
    }
  };

  const eliminarNomina = async (nomina) => {
    if (!window.confirm(`Eliminar nómina "${nomina.nombre}"? Las facturas quedarán sin asignar.`)) return;
    try {
      await api.delete(`/flujo-caja/cxp/nominas/${nomina.id}`);
      toast({ title: 'Nómina eliminada' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  // ============== IMPRIMIR NÓMINA (Carlos 08/05/2026) ==============
  const imprimirNomina = (nomina, modo = 'agrupado') => {
    if (!nomina) return;
    const isList = modo === 'listado';
    // Carlos 18/05/2026: el PDF debe respetar el mismo orden de columna y
    // el toggle "Mostrar pagadas" que tiene la pantalla. Antes imprimía
    // facturas crudas en orden de inserción y siempre incluía pagadas.
    const facturas = sortFacturas(
      (nomina.facturas || []).filter((f) => mostrarPagadas || f.estado !== 'pagada'),
      sortConfig,
    );
    const totalNomina = facturas.reduce((s, f) => s + (Number(f.monto) || 0), 0);
    const totalPagadas = facturas
      .filter((f) => f.estado === 'pagada')
      .reduce((s, f) => s + (Number(f.monto) || 0), 0);
    const totalPend = totalNomina - totalPagadas;
    const escapeHtml = (str) => String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const estadoMeta = (f) => {
      const venc = f.fecha_vencimiento_alegra || f.fecha_vencimiento || '';
      const isVencida = f.estado !== 'pagada' && venc && new Date(venc) < new Date();
      const label = f.estado === 'pagada' ? 'PAGADA' : (isVencida ? 'VENCIDA' : 'PENDIENTE');
      const bg = f.estado === 'pagada' ? '#d1fae5;color:#047857' : (isVencida ? '#fee2e2;color:#b91c1c' : '#fef3c7;color:#b45309');
      return { venc, isVencida, label, bg };
    };
    const conciliaCell = (f) => {
      const e = f.vb_estado || (f.concilia_estado === 'conciliada' ? 'conciliada' : (f.vb_forzado ? 'vb_forzado' : 'no_conciliada'));
      if (e === 'conciliada') return '<span class="cc cc-ok" title="Pareada en conciliación">&#10004;</span>';
      if (e === 'vb_forzado') return '<span class="cc cc-vb" title="V&deg;B&deg; forzado">V&deg;B&deg;</span>';
      return '<span class="cc cc-no" title="Sin conciliar">&#10008;</span>';
    };

    // ── Cuerpo: dos modos ──
    // (A) Agrupado: encabezado de proveedor 1 vez + subtotal (predeterminado).
    // (B) Listado: filas planas sin subtotales, con columna Proveedor.
    const lineCells = (f, withProv) => {
      const m = estadoMeta(f);
      // Renzo (oct-2026): la columna muestra la OC asociada a la factura (antes el RUT,
      // que era redundante con el RUT del encabezado del proveedor).
      const oc = f.dte_extra?.orden_compra || '—';
      return `
        <td class="c tipo">F</td>${withProv ? `
        <td class="prov">${escapeHtml(f.nombre_proveedor || f.razon_social || '-')}</td>` : ''}
        <td class="mono">${escapeHtml(f.folio || f.docmto || '-')}</td>
        <td class="mono">${escapeHtml(oc)}</td>
        <td>${f.fecha_emision || '-'}</td>
        <td class="${m.isVencida ? 'venc' : ''}">${m.venc || '-'}</td>
        <td class="r mono">${fmt(Number(f.monto) || 0)}</td>
        <td class="c">${conciliaCell(f)}</td>
        <td class="c"><span class="badge" style="background:${m.bg}">${m.label}</span></td>`;
    };

    let bodyHtml;
    if (isList) {
      // Respeta el orden/sort de pantalla.
      bodyHtml = facturas.map((f) => `<tr>${lineCells(f, true)}</tr>`).join('');
    } else {
      const groupsMap = {};
      facturas.forEach((f) => {
        const key = (f.nombre_proveedor || f.razon_social || '—').trim();
        (groupsMap[key] = groupsMap[key] || []).push(f);
      });
      bodyHtml = Object.keys(groupsMap)
        .sort((a, b) => a.localeCompare(b, 'es'))
        .map((prov) => {
          const items = groupsMap[prov];
          const subtotal = items.reduce((s, f) => s + (Number(f.monto) || 0), 0);
          const rut0 = items.find((x) => x.dte_extra?.rut_proveedor_real)?.dte_extra?.rut_proveedor_real || '';
          const head = `<tr class="grp"><td colspan="8">&#9656; ${escapeHtml(prov)}${rut0 ? ` <span class="grp-rut">&middot; ${escapeHtml(rut0)}</span>` : ''}</td></tr>`;
          const lines = items.map((f) => `<tr>${lineCells(f, false)}</tr>`).join('');
          const sub = `<tr class="subt"><td colspan="5" class="r">Subtotal ${escapeHtml(prov)} (${items.length})</td><td class="r mono">${fmt(subtotal)}</td><td colspan="2"></td></tr>`;
          return head + lines + sub;
        }).join('<tr class="grp-spacer"><td colspan="8"></td></tr>');
    }

    const theadHtml = isList
      ? `<th class="c" style="width:20px;">Tipo</th>
         <th>Proveedor</th>
         <th style="width:62px;">N&deg;</th>
         <th style="width:88px;">N&deg; OC</th>
         <th style="width:62px;">Emisi&oacute;n</th>
         <th style="width:62px;">Vencim.</th>
         <th style="width:80px;text-align:right;">Monto</th>
         <th class="c" style="width:44px;">Concilia</th>
         <th class="c" style="width:58px;">Estado</th>`
      : `<th class="c" style="width:20px;">Tipo</th>
         <th style="width:68px;">N&deg;</th>
         <th style="width:92px;">N&deg; OC</th>
         <th style="width:66px;">Emisi&oacute;n</th>
         <th style="width:66px;">Vencim.</th>
         <th style="width:84px;text-align:right;">Monto</th>
         <th class="c" style="width:46px;">Concilia</th>
         <th class="c" style="width:60px;">Estado</th>`;
    const NCOLS = isList ? 9 : 8;
    const totalRow = isList
      ? `<tr class="totals"><td colspan="6" style="text-align:right;">TOTAL N&Oacute;MINA (${facturas.length})</td><td class="r mono">${fmt(totalNomina)}</td><td colspan="2"></td></tr>`
      : `<tr class="totals"><td colspan="5" style="text-align:right;">TOTAL N&Oacute;MINA (${facturas.length})</td><td class="r mono">${fmt(totalNomina)}</td><td colspan="2"></td></tr>`;

    const html = `<!DOCTYPE html><html lang="es"><head>
<meta charset="utf-8"/>
<title>${escapeHtml(nomina.nombre || 'Nómina')} - CHIRINO STEEL</title>
<style>
  @page { size: letter portrait; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial,sans-serif; color:#1a202c; font-size:13px; margin:0; padding:8px; }
  .header { text-align:center; border-bottom:3px solid #1a365d; padding-bottom:8px; margin-bottom:12px; }
  .header h1 { font-size:21px; color:#1a365d; margin:0 0 4px; }
  .header h2 { font-size:17px; color:#2d3748; margin:0; }
  .header p { font-size:12px; color:#4a5568; margin:2px 0 0; }
  .summary { display:flex; gap:6px; margin:8px 0 12px; }
  .summary .box { flex:1; text-align:center; padding:6px; border:1px solid #cbd5e0; background:#f7fafc; border-radius:4px; }
  .summary .box.hl { background:#e9d8fd; }
  .summary .label { font-size:9px; color:#4a5568; text-transform:uppercase; }
  .summary .value { font-size:14px; font-weight:bold; font-family:monospace; margin-top:2px; }
  table { width:100%; border-collapse:collapse; font-size:12px; }
  th { background:#1a365d; color:#fff; padding:4px 5px; border:1px solid #1a365d; text-align:left; font-size:10px; text-transform:uppercase; }
  td { padding:3px 5px; border:1px solid #cbd5e0; vertical-align:top; }
  tbody tr:nth-child(even) { background:#fafbfc; }
  .r { text-align:right; }
  .c { text-align:center; }
  .mono { font-family:monospace; }
  .venc { color:#b91c1c; font-weight:600; }
  .badge { display:inline-block; padding:1px 6px; border-radius:6px; font-size:9px; font-weight:600; }
  .tipo { font-weight:700; color:#1a365d; }
  .prov { font-size:11px; }
  tr.grp td { background:#e2e8f0; color:#1a365d; font-weight:700; font-size:12px; padding:4px 6px; border-top:2px solid #1a365d; }
  .grp-rut { font-weight:500; color:#4a5568; font-family:monospace; }
  tr.subt td { background:#f1f5f9; font-weight:700; font-size:11px; color:#334155; border-bottom:1px solid #cbd5e0; }
  tr.grp-spacer td { border:0 !important; background:#fff !important; padding:0; height:10px; line-height:10px; font-size:0; }
  .cc { display:inline-block; min-width:14px; font-weight:800; font-size:14px; }
  .cc-ok { color:#15803d; }
  .cc-no { color:#dc2626; }
  .cc-vb { color:#b45309; background:#fef3c7; border:1px solid #f59e0b; border-radius:3px; padding:0 3px; font-size:9px; font-weight:700; }
  .legend { font-size:10px; color:#4a5568; margin:0 0 6px; display:flex; gap:10px; }
  .legend b { font-weight:700; }
  .totals td { background:#1a365d !important; color:#fff !important; font-weight:bold; padding:5px; }
  .footer { text-align:center; font-size:9px; color:#718096; margin-top:10px; border-top:1px solid #e2e8f0; padding-top:5px; }
  .toolbar { background:#1e3a8a; color:#fff; padding:6px 10px; display:flex; justify-content:space-between; align-items:center; margin-bottom:6px; border-radius:4px; }
  .toolbar button { background:#fff; color:#1e3a8a; border:0; padding:4px 12px; border-radius:3px; font-weight:600; cursor:pointer; margin-left:6px; }
  .toolbar button:hover { background:#fef3c7; }
  @media print { .toolbar { display:none !important; } body { padding:0; } }
</style>
</head>
<body>
  <div class="toolbar"><span><b>CHIRINO STEEL</b> · ${escapeHtml(nomina.nombre || 'Nómina')}</span><span><button onclick="window.print()">🖨️ Imprimir</button><button onclick="window.close()">✕ Cerrar</button></span></div>
  <div class="header">
    <h1>CHIRINO STEEL ING. Y CONT. SPA</h1>
    <h2>NÓMINA DE PAGO · ${escapeHtml(nomina.nombre || '')}</h2>
    <p>Fecha pago: <b>${fmtFecha(nomina.fecha_pago)}</b> · ${facturas.length} factura${facturas.length === 1 ? '' : 's'} · Estado: <b>${(nomina.estado || '').toUpperCase()}</b> · <b>${isList ? 'Listado' : 'Agrupado por proveedor'}</b></p>
  </div>
  <div class="summary">
    <div class="box"><div class="label">Total Nómina</div><div class="value">${fmt(totalNomina)}</div></div>
    <div class="box"><div class="label">Pagadas</div><div class="value" style="color:#15803d;">${fmt(totalPagadas)}</div></div>
    <div class="box"><div class="label">Pendientes</div><div class="value" style="color:#b91c1c;">${fmt(totalPend)}</div></div>
    <div class="box hl"><div class="label">% Avance</div><div class="value" style="color:#1d4ed8;">${totalNomina > 0 ? Math.round((totalPagadas / totalNomina) * 100) : 0}%</div></div>
  </div>
  <div class="legend">
    <span><b class="cc cc-ok">&#10004;</b> Conciliada</span>
    <span><b class="cc cc-no">&#10008;</b> Sin conciliar</span>
    <span><b class="cc cc-vb">V&deg;B&deg;</b> V&deg;B&deg; forzado (autorizada sin estado de cuenta)</span>
  </div>
  <table>
    <thead><tr>
      ${theadHtml}
    </tr></thead>
    <tbody>
      ${bodyHtml || `<tr><td colspan="${NCOLS}" style="text-align:center;color:#999;padding:10px;">Sin facturas</td></tr>`}
      ${totalRow}
    </tbody>
  </table>
  <div class="footer">Generado por Sistema de Gestion CHIRINO STEEL · ${new Date().toLocaleString('es-CL')}</div>
</body></html>`;

    // Estrategia robusta cross-browser via triggerPrint (detecta iOS/iPadOS
    // y abre nueva pestaña; en desktop usa iframe oculto same-origin).
    // triggerPrint YA dispara la impresión — no incluir <script>window.print()</script>
    // inline en el HTML o se imprime dos veces (Carlos, Jun 2026).
    triggerPrint(html);
  };

  // ============== EXPORTAR NÓMINA A EXCEL/CSV (Carlos 09/05/2026) ==============
  const exportarNominaExcel = (nomina) => {
    if (!nomina) return;
    // Carlos 18/05/2026: respetar mismo orden + filtro de pagadas que pantalla.
    const facturas = sortFacturas(
      (nomina.facturas || []).filter((f) => mostrarPagadas || f.estado !== 'pagada'),
      sortConfig,
    );
    const totalNomina = facturas.reduce((s, f) => s + (Number(f.monto) || 0), 0);
    const totalPagadas = facturas.filter((f) => f.estado === 'pagada').reduce((s, f) => s + (Number(f.monto) || 0), 0);

    exportToCSV({
      filename: `nomina_${(nomina.nombre || 'pago').replace(/\s+/g, '_')}`,
      title: `CHIRINO STEEL · Nómina de Pago · ${nomina.nombre || ''}`,
      summary: [
        { label: 'Fecha pago', value: fmtFecha(nomina.fecha_pago) || '-' },
        { label: 'Estado', value: (nomina.estado || '-').toUpperCase() },
        { label: 'Total facturas', value: facturas.length },
        { label: 'Total nómina (CLP)', value: Math.round(totalNomina) },
        { label: 'Total pagadas (CLP)', value: Math.round(totalPagadas) },
        { label: 'Total pendiente (CLP)', value: Math.round(totalNomina - totalPagadas) },
      ],
      columns: [
        { key: 'nombre_proveedor', label: 'Proveedor', format: (v, r) => v || r.razon_social || '' },
        { key: 'rut_proveedor', label: 'RUT' },
        { key: 'folio', label: 'Folio', format: (v, r) => v || r.docmto || '' },
        { key: 'fecha_emision', label: 'Fecha Emisión' },
        { key: 'fecha_vencimiento', label: 'Fecha Vencimiento', format: (v, r) => r.fecha_vencimiento_alegra || v || '' },
        { key: 'monto', label: 'Monto (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'estado', label: 'Estado', format: (v) => (v || '').toUpperCase() },
      ],
      rows: facturas,
    });
  };

  // ============== PAGAR FACTURA (con confirm + push Alegra) ==============
  const [confirmPay, setConfirmPay] = useState(null); // {factura} | null
  const [payBusy, setPayBusy] = useState(false);

  const pagarFactura = (factura) => setConfirmPay({ factura });

  // ============== V°B° FORZADO (autorizar sin conciliación) ==============
  const confirmVBForzado = async ({ forzar, motivo }) => {
    if (!vbDialog?.factura) return;
    try {
      await api.post(`/flujo-caja/cxp/facturas/${vbDialog.factura.id}/vb-forzado`, {
        forzar,
        motivo: motivo || null,
      });
      toast({
        title: forzar ? 'V°B° forzado aplicado' : 'V°B° forzado removido',
        description: forzar
          ? `Factura ${vbDialog.factura.folio} autorizada para pago.`
          : `Factura ${vbDialog.factura.folio} vuelve a su estado de conciliación.`,
      });
      setVbDialog(null);
      load();
    } catch (e) {
      toast({
        title: 'No se pudo actualizar el V°B°',
        description: e?.response?.data?.detail || 'Error inesperado',
        variant: 'destructive',
      });
    }
  };

  const ejecutarPago = async () => {
    if (!confirmPay?.factura) return;
    const f = confirmPay.factura;
    setPayBusy(true);
    try {
      const res = await api.patch(`/flujo-caja/cxp/facturas/${f.id}/pagar`, {
        fecha_pago_real: new Date().toISOString().slice(0, 10),
      });
      const alegra = res.data?.alegra || {};
      let desc = '';
      if (alegra.pushed) {
        desc = `✓ Registrado en Alegra (payment #${alegra.payment_id})`;
      } else if (alegra.reason) {
        desc = alegra.reason;
      } else if (alegra.error) {
        desc = `⚠️ Local OK, pero Alegra: ${alegra.error}`;
      }
      toast({ title: 'Factura pagada', description: desc });
      setConfirmPay(null);
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setPayBusy(false);
    }
  };

  // ============== PAGAR NÓMINA COMPLETA ==============
  const [confirmPayNomina, setConfirmPayNomina] = useState(null); // {nomina} | null
  const [payNominaBusy, setPayNominaBusy] = useState(false);

  const ejecutarPagoNomina = async () => {
    if (!confirmPayNomina?.nomina) return;
    const n = confirmPayNomina.nomina;
    setPayNominaBusy(true);
    try {
      const res = await api.post(`/flujo-caja/cxp/nominas/${n.id}/pagar`, {
        fecha_pago_real: new Date().toISOString().slice(0, 10),
      });
      const a = res.data?.alegra || {};
      const err = a.errores?.length || 0;
      toast({
        title: res.data?.message || 'Nómina pagada',
        description: `Alegra: ${a.pushed || 0}/${a.total_con_alegra_id || 0} pushed${err > 0 ? ` · ${err} errores` : ''}`,
      });
      if (err > 0) console.warn('[pagar-nomina] errores Alegra:', a.errores);
      setConfirmPayNomina(null);
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setPayNominaBusy(false);
    }
  };

  const toggleExp = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  // ============== CLEANUP RESIDUOS ALEGRA ==============
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupMontoMax, setCleanupMontoMax] = useState(5);

  const abrirCleanup = async () => {
    setCleanupOpen(true);
    setCleanupPreview(null);
    try {
      const res = await api.get(`/flujo-caja/cxp/residuos-preview?monto_max=${cleanupMontoMax}&dias_min_antiguedad=90`);
      setCleanupPreview(res.data);
    } catch (err) {
      toast({ title: 'Error preview', description: err.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const ejecutarCleanup = async () => {
    if (!cleanupPreview || cleanupPreview.count === 0) return;
    setCleanupBusy(true);
    try {
      const res = await api.post('/flujo-caja/cxp/cleanup-residuos', {
        monto_max: Number(cleanupMontoMax),
        dias_min_antiguedad: 90,
        dry_run: false,
      });
      toast({ title: res.data.message, description: res.data.errores?.length ? `${res.data.errores.length} errores. Ver consola.` : '' });
      if (res.data.errores?.length) console.warn('[cleanup-residuos] errores:', res.data.errores);
      setCleanupOpen(false);
      load();
    } catch (err) {
      toast({ title: 'Error cleanup', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setCleanupBusy(false);
    }
  };

  // Moverfactura
  const doMover = async (nominaDestinoId, fechaNueva) => {
    if (!moverDialog) return;
    try {
      await api.patch(`/flujo-caja/cxp/facturas/${moverDialog.factura.id}/mover`, {
        nomina_id: nominaDestinoId || null,
        fecha_pago_nueva: fechaNueva || null,
      });
      toast({ title: 'Factura movida' });
      setMoverDialog(null);
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  // ────────────────── Cmd+K: comandos contextuales (Iter 235.h) ──────────────────
  const totalNominas = (data?.nominas || []).length;
  const totalSinAsignar = (data?.sin_nomina || []).length;
  useRegisterCmdKCommands(
    'cxp-nominas',
    'CxP Nóminas (Viernes)',
    [
      // — Acciones bulk seleccionadas
      { id: 'cxp-pagar-seleccionadas', label: `Marcar pagadas ${selected.size} facturas seleccionadas`,
        sublabel: 'Confirma fecha y forma de pago',
        icon: 'DollarSign', keywords: 'pagar marcar pagadas seleccionadas bulk lote',
        run: () => setBatchPagarDialog(true),
        enabled: selected.size > 0 && !batchPagarDialog },
      { id: 'cxp-mover-seleccionadas', label: `Mover fecha de ${selected.size} facturas`,
        sublabel: 'Reasignar a otra nómina',
        icon: 'MoveRight', keywords: 'mover fecha cambiar nomina seleccionadas',
        run: () => setBatchMoverDialog({}),
        enabled: selected.size > 0 && !batchMoverDialog },
      { id: 'cxp-clear-selection', label: 'Limpiar selección',
        sublabel: `${selected.size} actualmente seleccionadas`,
        icon: 'X', keywords: 'limpiar deseleccionar reset',
        run: () => clearSelected(),
        enabled: selected.size > 0 },
      // — Acciones de nómina
      { id: 'cxp-auto-generar', label: 'Auto-generar nóminas (viernes)',
        sublabel: 'Distribuye facturas por fecha vencimiento',
        icon: 'Zap', keywords: 'auto generar nominas viernes distribuir',
        run: () => autoGenerar(),
        enabled: !generating },
      { id: 'cxp-sync-alegra', label: 'Sincronizar facturas desde Alegra',
        sublabel: 'Trae nuevas/actualizadas facturas',
        icon: 'RefreshCw', keywords: 'sincronizar alegra facturas refrescar',
        run: () => syncAlegra(),
        enabled: !syncing },
      { id: 'cxp-reconciliar-pagadas', label: 'Reconciliar pagadas con Alegra',
        sublabel: 'Revierte pagadas-locales abiertas/eliminadas en Alegra',
        icon: 'ShieldCheck', keywords: 'reconciliar pagadas reabiertas eliminadas alegra revertir mirror',
        run: () => reconciliarPagadas(),
        enabled: !reconciling },
      { id: 'cxp-refresh', label: 'Refrescar nóminas',
        icon: 'RefreshCw', keywords: 'refrescar recargar actualizar reload',
        run: () => load() },
      // — Vistas / filtros
      { id: 'cxp-toggle-pagadas', label: mostrarPagadas ? 'Ocultar facturas pagadas' : 'Mostrar facturas pagadas',
        icon: 'Filter', keywords: 'mostrar ocultar pagadas filtro',
        run: () => setMostrarPagadas((v) => !v) },
      { id: 'cxp-collapse-all', label: 'Colapsar todas las nóminas',
        sublabel: `${totalNominas} nóminas`,
        icon: 'ChevronRight', keywords: 'colapsar cerrar todas nominas',
        run: () => setExpanded({}),
        enabled: Object.values(expanded).some(Boolean) },
      { id: 'cxp-expand-all', label: 'Expandir todas las nóminas',
        sublabel: `${totalNominas} nóminas`,
        icon: 'ChevronDown', keywords: 'expandir abrir todas nominas',
        run: () => {
          const exp = {};
          (data?.nominas || []).forEach((n) => { exp[n.id] = true; });
          setExpanded(exp);
        },
        enabled: totalNominas > 0 },
      // — Mantenimiento
      { id: 'cxp-cleanup', label: 'Limpiar facturas residuales (cleanup)',
        sublabel: 'Cierra montos pequeños',
        icon: 'Trash2', keywords: 'limpiar cleanup residuales pequenos cerrar',
        run: () => setCleanupOpen(true) },
    ],
    [selected.size, batchPagarDialog, batchMoverDialog, generating, syncing, reconciling, mostrarPagadas, totalNominas, totalSinAsignar, expanded, data],
  );

  return (
    <div className="space-y-4" data-testid="cxp-panel">
      {/* Header */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px]">
            <p className="text-xs text-muted-foreground">Total pendiente por pagar</p>
            <p className="text-2xl font-bold tabular-nums text-red-600">{fmt(data.total_pendiente)}</p>
          </div>
          <Button size="sm" variant="outline" disabled={syncing} onClick={syncAlegra} className="gap-1.5" data-testid="btn-sync-cxp">
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando…' : 'Sincronizar Alegra'}
          </Button>
          <Button size="sm" variant="outline" disabled={reconciling} onClick={reconciliarPagadas} className="gap-1.5 border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="btn-reconciliar-pagadas" title="Revierte a pendiente las facturas pagadas localmente que en Alegra están abiertas o fueron eliminadas">
            <ShieldCheck className={`h-3.5 w-3.5 ${reconciling ? 'animate-pulse' : ''}`} />
            {reconciling ? 'Reconciliando…' : 'Reconciliar pagadas'}
          </Button>
          <Button size="sm" disabled={generating} onClick={autoGenerar} className="gap-1.5 bg-amber-600 hover:bg-amber-700" data-testid="btn-auto-gen">
            <Zap className="h-3.5 w-3.5" />
            {generating ? 'Generando…' : 'Auto-generar nóminas'}
          </Button>
          <Button size="sm" variant="outline" onClick={abrirCleanup} className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50" data-testid="btn-cleanup-residuos" title="Cierra residuos pequeños ($1-$2) en Alegra">
            <Trash2 className="h-3.5 w-3.5" />
            Limpiar residuos
          </Button>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none ml-auto" data-testid="toggle-mostrar-pagadas">
            <input
              type="checkbox"
              checked={mostrarPagadas}
              onChange={(e) => setMostrarPagadas(e.target.checked)}
              className="accent-emerald-600"
            />
            Mostrar pagadas
          </label>
          {/* Iter 236.l (Carlos): buscador local — filtra dentro de las nóminas */}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar proveedor, folio o RUT…"
            className="text-xs border rounded px-2 py-1 w-56"
            data-testid="cxp-search-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="text-xs text-gray-500 hover:text-red-600"
              data-testid="cxp-search-clear"
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </CardContent>
      </Card>

      {/* Banner: facturas pagadas con error de sync a Alegra (extraído Iter 236.m.14) */}
      <SyncErrorsBanner
        facturas={pagadasConError}
        autoFixing={autoFixing}
        retryingAll={retryingAll}
        onAutoFix={autoFixSyncErrors}
        onRetryAll={reintentarTodosErrores}
        onRetryOne={reintentarAlegraFactura}
      />

      {/* Banner: facturas DUPLICADAS en Alegra (Iter 237 — detector folio+proveedor) */}
      <DuplicadosAlegraBanner
        data={duplicados}
        loading={duplicadosLoading}
        open={duplicadosOpen}
        onOpen={() => setDuplicadosOpen(true)}
        onClose={() => setDuplicadosOpen(false)}
        onRefresh={() => loadDuplicados(true)}
      />

      {/* Facturas sin nómina */}
      {data.sin_nomina?.length > 0 && (
        <Card className="border-amber-400 bg-amber-50/60 dark:bg-amber-950/30">
          <CardContent className="p-3">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1 mb-2">
              <AlertTriangle className="h-4 w-4" /> {data.sin_nomina.length} facturas sin asignar a nómina
            </p>
            <p className="text-[11px] text-amber-600">Presiona "Auto-generar nóminas" para asignarlas al viernes de pago correspondiente.</p>
          </CardContent>
        </Card>
      )}

      {/* Nóminas — Iter 236.m (Carlos): cuando hay búsqueda activa, mostrar UN solo
          listado unificado con TODAS las facturas matcheadas en TODAS las nóminas. */}
      {loading ? (
        <p className="text-center text-muted-foreground py-8">Cargando…</p>
      ) : data.nominas.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Sin nóminas por pagar. Sincroniza con Alegra y presiona "Auto-generar nóminas".</CardContent></Card>
      ) : searchTerm.trim() ? (
        (() => {
          const q = searchTerm.trim().toLowerCase();
          // Aplanar todas las facturas matching de todas las nóminas, llevando
          // info de la nómina origen para mostrar en columna extra.
          const flat = [];
          for (const n of data.nominas) {
            for (const f of (n.facturas || [])) {
              if (!mostrarPagadas && f.estado === 'pagada') continue;
              const match =
                (f.nombre_proveedor || '').toLowerCase().includes(q) ||
                (f.folio || '').toLowerCase().includes(q) ||
                (f.rut_proveedor || '').toLowerCase().includes(q) ||
                (f.dte_extra?.rut_proveedor_real || '').toLowerCase().includes(q);
              if (match) {
                flat.push({ ...f, _nomina_id: n.id, _nomina_nombre: n.nombre, _nomina_fecha_pago: n.fecha_pago });
              }
            }
          }
          const sorted = sortFacturas(flat, sortConfig);
          const total = sorted.reduce((s, f) => s + (f.monto || 0), 0);
          return (
            <Card data-testid="cxp-search-results-card" className="border-blue-300 bg-blue-50/30 dark:bg-blue-950/20">
              <CardHeader className="p-3">
                <CardTitle className="text-sm flex flex-wrap items-center gap-2">
                  🔎 <span className="text-blue-700">Resultados de búsqueda: "{searchTerm}"</span>
                  <div className="flex-1" />
                  <span className="text-xs text-muted-foreground">{sorted.length} factura{sorted.length === 1 ? '' : 's'}</span>
                  <span className="text-sm font-bold tabular-nums">{fmt(total)}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {sorted.length === 0 ? (
                  <p className="text-center text-muted-foreground py-6 text-sm">Sin coincidencias en ninguna nómina.</p>
                ) : (
                  <StickyHScroll>
                    <table className="w-full text-xs">
                      <thead className="bg-muted/40">
                        <tr>
                          <th className="text-left p-2 cursor-pointer select-none hover:bg-muted/70" onClick={() => toggleSort('nombre_proveedor')} data-testid="th-sort-proveedor-search">
                            Proveedor
                            <SortIcon active={sortConfig.key === 'nombre_proveedor'} direction={sortConfig.direction} />
                          </th>
                          <th className="text-left p-2 w-24 cursor-pointer select-none hover:bg-muted/70" onClick={() => toggleSort('folio')}>
                            Folio
                            <SortIcon active={sortConfig.key === 'folio'} direction={sortConfig.direction} />
                          </th>
                          <th className="text-left p-2 w-32">Nómina</th>
                          <th className="text-left p-2 w-28 cursor-pointer select-none hover:bg-muted/70" onClick={() => toggleSort('fecha_vencimiento_alegra')}>
                            Vence Alegra
                            <SortIcon active={sortConfig.key === 'fecha_vencimiento_alegra'} direction={sortConfig.direction} />
                          </th>
                          <th className="text-left p-2 w-28 cursor-pointer select-none hover:bg-muted/70" onClick={() => toggleSort('fecha_vencimiento')}>
                            Vence ERP
                            <SortIcon active={sortConfig.key === 'fecha_vencimiento'} direction={sortConfig.direction} />
                          </th>
                          <th className="text-right p-2 w-28 cursor-pointer select-none hover:bg-muted/70" onClick={() => toggleSort('monto')}>
                            Monto
                            <SortIcon active={sortConfig.key === 'monto'} direction={sortConfig.direction} />
                          </th>
                          <th className="text-center p-2 w-16">Estado</th>
                          <th className="text-center p-2 w-20">Docs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map((f) => {
                          const fAlegra = f.fecha_vencimiento_alegra || f.fecha_vencimiento;
                          const fERP = f.fecha_vencimiento;
                          const dias = diasVencida(fERP);
                          const isVencida = f.estado === 'pendiente' && dias > 0;
                          return (
                            <tr key={`s-${f.id}`} className={`border-t hover:bg-muted/30 ${isVencida ? 'bg-red-50/60 dark:bg-red-950/30' : ''}`} data-testid={`search-row-${f.id}`}>
                              <td className="p-2 truncate max-w-[260px]" title={f.nombre_proveedor}>
                                {f.nombre_proveedor}
                                {f.es_ee_rr && (
                                  <span className="ml-1 text-[9px] font-semibold bg-amber-100 text-amber-700 border border-amber-300 rounded px-1 py-0.5" title={`Empresa relacionada: ${f.receptor_nombre || f.receptor_rut} — se paga como Egreso a "Ctas por Cobrar EE.RR."`} data-testid={`ee-rr-badge-search-${f.id}`}>EE.RR.</span>
                                )}
                              </td>
                              <td className="p-2 font-mono">
                                <div className="flex flex-col">
                                  <span>{f.folio}</span>
                                  {/* Iter 236.r: badge NC aplicada */}
                                  {f.ncs_aplicadas_count > 0 && (
                                    <span
                                      className="text-[9px] text-orange-700 font-semibold bg-orange-50 border border-orange-200 rounded px-1 py-0.5 mt-0.5 inline-flex items-center gap-0.5 max-w-[110px] truncate"
                                      title={`NC${f.ncs_aplicadas_count > 1 ? 's' : ''} aplicada${f.ncs_aplicadas_count > 1 ? 's' : ''}: ${f.ncs_aplicadas.map((nc) => `${nc.folio_nc} ($${(nc.monto || 0).toLocaleString('es-CL')})${nc.motivo ? ` — ${nc.motivo}` : ''}`).join('\n')}`}
                                      data-testid={`nc-badge-search-${f.id}`}
                                    >
                                      🔻 NC -${(f.ncs_aplicadas_monto_total || 0).toLocaleString('es-CL')}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-2 text-[10px] text-muted-foreground">
                                <div className="flex flex-col">
                                  <span className="truncate">{f._nomina_nombre || '—'}</span>
                                  {f._nomina_fecha_pago && <span className="text-[9px]">{fmtFecha(f._nomina_fecha_pago)}</span>}
                                </div>
                              </td>
                              <td className="p-2 text-muted-foreground">{fmtFecha(fAlegra)}</td>
                              <td className={`p-2 ${isVencida ? 'text-red-700 font-semibold' : ''}`}>{fmtFecha(fERP)}</td>
                              <td className={`p-2 text-right tabular-nums font-medium ${isVencida ? 'text-red-700' : ''}`}>{fmt(f.monto)}</td>
                              <td className="p-2 text-center">
                                <AlegraStatusBadge
                                  contabilizada_en_alegra={Boolean(f.alegra_id)}
                                  contabilizada={Boolean(f.alegra_id)}
                                  estado_pago={f.estado}
                                  alegra_bill_id={f.alegra_id}
                                  en_nomina={Boolean(f.nomina_id)}
                                  liberada_para_pago={f.liberada_para_pago}
                                />
                              </td>
                              <td className="p-2 text-center">
                                <div className="inline-flex items-center gap-1">
                                  <DteDocsActions
                                    folio={f.folio}
                                    rutEmisor={f.dte_extra?.rut_proveedor_real || ''}
                                    numeroOc={f.dte_extra?.orden_compra || ''}
                                    numeroRecepcion={f.numero_recepcion || ''}
                                  />
                                  {f.alegra_id && (
                                    <a
                                      href={`https://app.alegra.com/bill/view/id/${f.alegra_id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="p-1 rounded hover:bg-violet-50 text-violet-600 hover:text-violet-800 transition-colors"
                                      title="Ver en Alegra"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </StickyHScroll>
                )}
              </CardContent>
            </Card>
          );
        })()
      ) : (
        data.nominas.map(n => {
          const esAtrasadas = !n.fecha_pago;
          const isExp = !!expanded[n.id];
          // Cuenta facturas con vencimiento real anterior a hoy (aún si la nómina es futura)
          const facturasVencidas = (n.facturas || []).filter(
            f => f.estado === 'pendiente' && diasVencida(f.fecha_vencimiento) > 0
          );
          const tieneVencidas = !esAtrasadas && facturasVencidas.length > 0;
          const totalVencido = facturasVencidas.reduce((s, f) => s + (f.monto || 0), 0);
          return (
            <Card
              key={n.id}
              className={esAtrasadas ? 'border-red-400' : tieneVencidas ? 'border-red-400 bg-red-50/30 dark:bg-red-950/20' : ''}
              data-testid={`nomina-${n.id}`}
            >
              <CardHeader className="p-3 cursor-pointer" onClick={() => toggleExp(n.id)}>
                <CardTitle className="text-sm flex flex-wrap items-center gap-2">
                  {isExp ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <span className={esAtrasadas ? 'text-red-600 font-bold' : tieneVencidas ? 'text-red-700 font-bold' : ''}>
                    {esAtrasadas ? '⚠️ ' : tieneVencidas ? '🔴 ' : '📆 '}{n.nombre}
                  </span>
                  {!esAtrasadas && <span className="text-xs text-muted-foreground">({fmtFecha(n.fecha_pago)})</span>}
                  {tieneVencidas && (
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-600 text-white tabular-nums"
                      title={`${facturasVencidas.length} factura(s) con fecha de vencimiento real anterior a hoy. Total vencido: ${fmt(totalVencido)}`}
                      data-testid={`badge-vencidas-${n.id}`}
                    >
                      {facturasVencidas.length} vencida{facturasVencidas.length === 1 ? '' : 's'} · {fmt(totalVencido)}
                    </span>
                  )}
                  <div className="flex-1" />
                  <span className="text-xs">{n.num_facturas} fact.</span>
                  <span className="text-sm font-bold tabular-nums">{fmt(n.total)}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${n.estado === 'pagada' ? 'bg-emerald-500/15 text-emerald-700' : n.estado === 'falta_egreso_alegra' ? 'bg-orange-500/20 text-orange-700 font-semibold' : n.estado === 'parcial' ? 'bg-amber-500/15 text-amber-700' : 'bg-red-500/15 text-red-700'}`} data-testid={`badge-estado-nomina-${n.id}`}>
                    {n.estado === 'falta_egreso_alegra' ? `Falta Egreso en Alegra${n.num_sin_contabilizar ? ` (${n.num_sin_contabilizar})` : ''}` : n.estado}
                  </span>
                </CardTitle>
              </CardHeader>
              {isExp && (
                <CardContent className="p-0">
                  <div className="flex flex-wrap gap-2 px-3 pb-2">
                    {!esAtrasadas && (
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); cambiarFecha(n); }} className="text-xs gap-1 h-7">
                        <Calendar className="h-3 w-3" /> Cambiar fecha
                      </Button>
                    )}
                    {n.estado !== 'pagada' && (
                    <Button size="sm" className="text-xs gap-1 h-7 bg-emerald-600 hover:bg-emerald-700" onClick={(e) => { e.stopPropagation(); pagarNomina(n); }} title="Marcar todas pagadas y registrar pagos en Alegra" data-testid={`btn-pagar-nomina-${n.id}`}>
                      <Check className="h-3 w-3" /> Marcar todas pagadas
                    </Button>
                    )}
                    {n.estado === 'falta_egreso_alegra' && (
                    <Button size="sm" className="text-xs gap-1 h-7 bg-orange-600 hover:bg-orange-700" disabled={contabBusyId === n.id} onClick={(e) => { e.stopPropagation(); reintentarContabilizacion(n); }} title="Reintentar la creación en Alegra de los egresos contables que faltan" data-testid={`btn-reintentar-contab-${n.id}`}>
                      <RefreshCw className={`h-3 w-3 ${contabBusyId === n.id ? 'animate-spin' : ''}`} /> {contabBusyId === n.id ? 'Reintentando…' : `Reintentar contabilización${n.num_sin_contabilizar ? ` (${n.num_sin_contabilizar})` : ''}`}
                    </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" className="text-xs gap-1 h-7" onClick={(e) => e.stopPropagation()} title="Imprimir nómina" data-testid={`btn-print-nomina-${n.id}`}>
                          <Printer className="h-3 w-3" /> Imprimir <ChevronDown className="h-3 w-3 opacity-60" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); imprimirNomina(n, 'agrupado'); }} data-testid={`btn-print-agrupado-${n.id}`}>
                          Agrupado por proveedor (con subtotales)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); imprimirNomina(n, 'listado'); }} data-testid={`btn-print-listado-${n.id}`}>
                          Listado (sin subtotales)
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button size="sm" variant="outline" className="text-xs gap-1 h-7 text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={(e) => { e.stopPropagation(); exportarNominaExcel(n); }} title="Exportar a Excel/CSV" data-testid={`btn-excel-nomina-${n.id}`}>
                      <FileSpreadsheet className="h-3 w-3" /> Excel
                    </Button>
                    <Button size="sm" variant="outline" disabled={tefBusyId === n.id} className="text-xs gap-1 h-7 text-indigo-700 border-indigo-300 hover:bg-indigo-50" onClick={(e) => { e.stopPropagation(); generarTef(n); }} title="Generar archivo de nómina TEF (Banco de Chile) con las facturas seleccionadas (checkbox)" data-testid={`btn-tef-nomina-${n.id}`}>
                      <Landmark className="h-3 w-3" /> {tefBusyId === n.id ? 'Generando…' : 'Generar Nómina'}
                    </Button>
                    {n.estado !== 'pagada' && (
                    <Button size="sm" variant="outline" className="text-xs gap-1 h-7 text-red-600" onClick={(e) => { e.stopPropagation(); eliminarNomina(n); }}>
                      <Trash2 className="h-3 w-3" /> Eliminar nómina
                    </Button>
                    )}
                  </div>
                  <StickyHScroll>
                    {/* — */}
                    <table className="w-full text-xs">
                      <thead className="bg-muted/40">
                        <tr>
                          <th className="text-center p-2 w-8">
                            {(() => {
                              const ids = (n.facturas || []).filter((f) => f.estado === 'pendiente').map((f) => f.id);
                              const allSel = ids.length > 0 && ids.every((id) => selected.has(id));
                              return (
                                <input
                                  type="checkbox"
                                  checked={allSel}
                                  onChange={() => toggleSelectAllNomina(n, allSel)}
                                  data-testid={`chk-all-${n.id}`}
                                  title="Seleccionar todas pendientes"
                                />
                              );
                            })()}
                          </th>
                          <th
                            className="text-left p-2 cursor-pointer select-none hover:bg-muted/70"
                            onClick={() => toggleSort('nombre_proveedor')}
                            data-testid="th-sort-proveedor"
                            title="Ordenar por proveedor (click para alternar)"
                          >
                            Proveedor
                            <SortIcon active={sortConfig.key === 'nombre_proveedor'} direction={sortConfig.direction} />
                          </th>
                          <th
                            className="text-left p-2 w-24 cursor-pointer select-none hover:bg-muted/70"
                            onClick={() => toggleSort('folio')}
                            data-testid="th-sort-folio"
                          >
                            Folio
                            <SortIcon active={sortConfig.key === 'folio'} direction={sortConfig.direction} />
                          </th>
                          <th
                            className="text-left p-2 w-28 cursor-pointer select-none hover:bg-muted/70"
                            title="Fecha vencimiento original de Alegra · click para ordenar"
                            onClick={() => toggleSort('fecha_vencimiento_alegra')}
                            data-testid="th-sort-vence-alegra"
                          >
                            Vence Alegra
                            <SortIcon active={sortConfig.key === 'fecha_vencimiento_alegra'} direction={sortConfig.direction} />
                          </th>
                          <th
                            className="text-left p-2 w-28 cursor-pointer select-none hover:bg-muted/70"
                            title="Fecha de pago programada en el ERP (modificable) · click para ordenar"
                            onClick={() => toggleSort('fecha_vencimiento')}
                            data-testid="th-sort-vence-erp"
                          >
                            Vence ERP
                            <SortIcon active={sortConfig.key === 'fecha_vencimiento'} direction={sortConfig.direction} />
                          </th>
                          <th
                            className="text-center p-2 w-24 cursor-pointer select-none hover:bg-muted/70"
                            title="Días atrasado respecto al acuerdo comercial en Alegra (fecha_venc_alegra). Amber 1-15 · Orange 16-30 · Rojo >30."
                            onClick={() => toggleSort('dias_atraso')}
                            data-testid="th-sort-dias-atraso"
                          >
                            Días plazo
                            <SortIcon active={sortConfig.key === 'dias_atraso'} direction={sortConfig.direction} />
                          </th>
                          <th
                            className="text-right p-2 w-28 cursor-pointer select-none hover:bg-muted/70"
                            onClick={() => toggleSort('monto')}
                            data-testid="th-sort-monto"
                          >
                            Monto
                            <SortIcon active={sortConfig.key === 'monto'} direction={sortConfig.direction} />
                          </th>
                          <th
                            className="text-center p-2 w-16 cursor-pointer select-none hover:bg-muted/70"
                            onClick={() => toggleSort('estado')}
                            data-testid="th-sort-estado"
                          >
                            Estado
                            <SortIcon active={sortConfig.key === 'estado'} direction={sortConfig.direction} />
                          </th>
                          <th className="text-center p-2 w-28">Forma pago</th>
                          <th className="text-center p-2 w-20" title="Visualizar Factura DTE · OC · Recepción">Docs</th>
                          <th className="text-center p-2 w-32">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortFacturas(
                          (n.facturas || []).filter((f) => {
                            if (!mostrarPagadas && f.estado === 'pagada') return false;
                            const q = searchTerm.trim().toLowerCase();
                            if (!q) return true;
                            // Iter 236.l: buscador local por proveedor / folio / RUT
                            return (
                              (f.nombre_proveedor || '').toLowerCase().includes(q) ||
                              (f.folio || '').toLowerCase().includes(q) ||
                              (f.rut_proveedor || '').toLowerCase().includes(q) ||
                              (f.dte_extra?.rut_proveedor_real || '').toLowerCase().includes(q)
                            );
                          }),
                          sortConfig,
                        ).map(f => {
                          // Punto 3 Carlos: días de atraso según acuerdo comercial Alegra (fecha_venc_alegra).
                          const fAlegra = f.fecha_vencimiento_alegra || f.fecha_vencimiento;
                          const fERP = f.fecha_vencimiento;
                          const diasAtraso = f.estado === 'pendiente' ? diasVencida(fAlegra) : 0;
                          const dias = diasVencida(fERP);
                          const isVencida = f.estado === 'pendiente' && dias > 0;
                          const fechaModificada = fAlegra && fERP && fAlegra !== fERP;
                          const isSel = selected.has(f.id);
                          const bucket = diasAtrasoBucket(diasAtraso);
                          return (
                          <tr
                            key={f.id}
                            className={`border-t hover:bg-muted/30 ${isVencida ? 'bg-red-50/60 dark:bg-red-950/30' : ''} ${isSel ? 'bg-blue-50/70 dark:bg-blue-950/40' : ''}`}
                            data-testid={`factura-${f.id}`}
                          >
                            <td className="p-2 text-center">
                              {f.estado === 'pendiente' && (
                                <input
                                  type="checkbox"
                                  checked={isSel}
                                  onChange={() => toggleSelected(f.id)}
                                  data-testid={`chk-fact-${f.id}`}
                                />
                              )}
                            </td>
                            <td className="p-2 truncate max-w-[260px]" title={(() => {
                              const ex = f.dte_extra || {};
                              const parts = [f.nombre_proveedor];
                              if (ex.emisor_direccion) parts.push(`\n${ex.emisor_direccion}`);
                              if (ex.emisor_comuna) parts.push(` · ${ex.emisor_comuna}`);
                              if (ex.emisor_ciudad && ex.emisor_ciudad !== ex.emisor_comuna) parts.push(` · ${ex.emisor_ciudad}`);
                              if (ex.medio_pago) parts.push(`\nForma pago DTE: ${ex.medio_pago}`);
                              return parts.join('');
                            })()}>
                              <div className="flex flex-col">
                                <span className="truncate">
                                  {f.nombre_proveedor}
                                  {f.es_ee_rr && (
                                    <span className="ml-1 text-[9px] font-semibold bg-amber-100 text-amber-700 border border-amber-300 rounded px-1 py-0.5" title={`Empresa relacionada: ${f.receptor_nombre || f.receptor_rut} — se paga como Egreso a "Ctas por Cobrar EE.RR."`} data-testid={`ee-rr-badge-${f.id}`}>EE.RR.</span>
                                  )}
                                </span>
                                {f.dte_extra?.emisor_comuna && (
                                  <span className="text-[9px] text-muted-foreground truncate" title={`${f.dte_extra.emisor_direccion || ''} · ${f.dte_extra.emisor_comuna}`}>
                                    📍 {f.dte_extra.emisor_comuna}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-2 font-mono">
                              <div className="flex flex-col">
                                <span>{f.folio}</span>
                                {f.dte_extra?.orden_compra && (
                                  <span className="text-[9px] text-green-700 font-medium" title={`OC vinculada: ${f.dte_extra.orden_compra}`}>
                                    OC {f.dte_extra.orden_compra}
                                  </span>
                                )}
                                {f.dte_extra?.medio_pago && (
                                  <span className="text-[9px] text-blue-600 truncate max-w-[90px]" title={`Forma pago DTE: ${f.dte_extra.medio_pago}`}>
                                    💳 {f.dte_extra.medio_pago}
                                  </span>
                                )}
                                {/* Iter 236.r (Carlos): badge NC aplicada */}
                                {f.ncs_aplicadas_count > 0 && (
                                  <span
                                    className="text-[9px] text-orange-700 font-semibold bg-orange-50 border border-orange-200 rounded px-1 py-0.5 mt-0.5 inline-flex items-center gap-0.5 max-w-[110px] truncate"
                                    title={`NC${f.ncs_aplicadas_count > 1 ? 's' : ''} aplicada${f.ncs_aplicadas_count > 1 ? 's' : ''}: ${f.ncs_aplicadas.map((nc) => `${nc.folio_nc} ($${(nc.monto || 0).toLocaleString('es-CL')})${nc.motivo ? ` — ${nc.motivo}` : ''}`).join('\n')}`}
                                    data-testid={`nc-badge-${f.id}`}
                                  >
                                    🔻 NC -${(f.ncs_aplicadas_monto_total || 0).toLocaleString('es-CL')}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-2 text-muted-foreground" title="Fecha original Alegra">
                              {fmtFecha(fAlegra)}
                            </td>
                            <td className={`p-2 ${isVencida ? 'text-red-700 font-semibold' : ''} ${fechaModificada ? 'font-semibold' : ''}`} title={fechaModificada ? `Modificada en ERP (Alegra: ${fAlegra})` : 'Mismo que Alegra'}>
                              {fmtFecha(fERP)}
                              {fechaModificada && <span className="ml-1 text-[9px] text-blue-700 dark:text-blue-300" title="Fecha movida en ERP">★</span>}
                              {isVencida && (
                                <span
                                  className="ml-1 text-[9px] px-1 py-0.5 rounded bg-red-600 text-white tabular-nums"
                                  title={`Vencida hace ${dias} día${dias === 1 ? '' : 's'}`}
                                  data-testid={`chip-vencida-${f.id}`}
                                >
                                  -{dias}d
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-center" data-testid={`cell-dias-atraso-${f.id}`}>
                              {f.estado === 'pendiente' ? (
                                <span
                                  className={`text-[10px] px-1.5 py-0.5 rounded tabular-nums ${bucket.color}`}
                                  title={diasAtraso > 0
                                    ? `Atrasada ${diasAtraso} día${diasAtraso === 1 ? '' : 's'} vs acuerdo comercial Alegra (${fAlegra || 's/f'})`
                                    : 'Dentro del plazo comercial Alegra'}
                                >
                                  {bucket.label}
                                </span>
                              ) : (
                                <span className="text-[10px] text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className={`p-2 text-right tabular-nums font-medium ${isVencida ? 'text-red-700' : ''}`}>{fmt(f.monto)}</td>
                            <td className="p-2 text-center">
                              {/* Iter 235.e (Carlos): badge unificado de estado en Alegra + pago + nómina */}
                              <div className="flex flex-col items-center gap-0.5">
                                <AlegraStatusBadge
                                  contabilizada_en_alegra={Boolean(f.alegra_id)}
                                  contabilizada={Boolean(f.alegra_id)}
                                  estado_pago={f.estado}
                                  alegra_bill_id={f.alegra_id}
                                  en_nomina={Boolean(f.nomina_id)}
                                  liberada_para_pago={f.liberada_para_pago}
                                />
                                {isVencida && f.estado !== 'pagada' && (
                                  <span className="text-[9px] px-1 py-0 rounded bg-red-600 text-white font-semibold">vencida</span>
                                )}
                                <ConciliaBadge
                                  factura={f}
                                  onForce={canForceVB && f.estado === 'pendiente' ? () => setVbDialog({ factura: f }) : undefined}
                                />
                              </div>
                            </td>
                            <td className="p-2 text-center">
                              <RowFormaPagoSelect
                                factura={f}
                                onChanged={() => { load(); loadErrorsBanner(); }}
                                disabled={f.estado === 'pagada'}
                              />
                            </td>
                            {/* Iter 235.d (Carlos): Docs — DTE / OC / Recep */}
                            <td className="p-2 text-center">
                              <div className="inline-flex items-center gap-1">
                                <DteDocsActions
                                  folio={f.folio}
                                  rutEmisor={f.dte_extra?.rut_proveedor_real || ''}
                                  numeroOc={f.dte_extra?.orden_compra || ''}
                                  numeroRecepcion={f.numero_recepcion || ''}
                                />
                                {/* Iter 236.j (Carlos): link directo a Alegra */}
                                {f.alegra_id && (
                                  <a
                                    href={`https://app.alegra.com/bill/view/id/${f.alegra_id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="p-1 rounded hover:bg-violet-50 text-violet-600 hover:text-violet-800 transition-colors"
                                    title="Ver en Alegra"
                                    data-testid={`open-alegra-${f.folio}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                  </a>
                                )}
                              </div>
                            </td>
                            <td className="p-2 text-center">
                              {f.estado === 'pendiente' && (
                                <div className="flex justify-center gap-1">
                                  <Button size="icon" variant="ghost" className="h-6 w-6 text-blue-600" title="Mover a otra nómina"
                                    onClick={() => setMoverDialog({ factura: f, nominas: data.nominas.filter(x => x.id !== n.id) })}
                                    data-testid={`btn-mover-${f.id}`}>
                                    <MoveRight className="h-3 w-3" />
                                  </Button>
                                  {canForceVB && f.vb_estado !== 'conciliada' && (
                                    <Button size="icon" variant="ghost"
                                      className={`h-6 w-6 ${f.vb_forzado ? 'text-amber-600' : 'text-muted-foreground hover:text-amber-600'}`}
                                      title={f.vb_forzado ? 'V°B° forzado (click para gestionar)' : 'Forzar V°B° (autorizar sin conciliación)'}
                                      onClick={() => setVbDialog({ factura: f })}
                                      data-testid={`btn-vb-forzar-${f.id}`}>
                                      <Stamp className="h-3 w-3" />
                                    </Button>
                                  )}
                                  <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-600" title="Marcar pagada (y registrar en Alegra)"
                                    onClick={() => pagarFactura(f)} data-testid={`btn-pagar-${f.id}`}>
                                    <Check className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </StickyHScroll>
                </CardContent>
              )}
            </Card>
          );
        })
      )}

      {/* Barra de acción batch (sticky cuando hay selección) — extraído Iter 236.m.14 */}
      <BatchActionsBar
        count={selected.size}
        busy={batchBusy}
        selectedIds={Array.from(selected)}
        onClear={clearSelected}
        onAbrirMover={() => setBatchMoverDialog({ nominas: data.nominas })}
        onAbrirPagar={() => setBatchPagarDialog(true)}
        onAbrirNominaEspecial={() => setNominaEspecialDialog({ abierto: true })}
        onFormaPagoApplied={() => {
          clearSelected();
          load();
        }}
      />

      {/* Dialog confirmar pago batch */}
      <BatchPagarDialog
        open={!!batchPagarDialog}
        busy={batchBusy}
        count={selected.size}
        onClose={() => setBatchPagarDialog(false)}
        onConfirm={ejecutarBatchPagar}
      />

      {/* Dialog mover batch */}
      <BatchMoverDialog
        open={!!batchMoverDialog}
        busy={batchBusy}
        count={selected.size}
        nominas={batchMoverDialog?.nominas || []}
        fechaCustom={batchMoverFecha}
        setFechaCustom={setBatchMoverFecha}
        onClose={() => setBatchMoverDialog(null)}
        onMover={ejecutarBatchMover}
      />

      {/* Iter 236.n (Carlos): dialog crear nómina especial con facturas seleccionadas */}
      <BatchNominaEspecialDialog
        open={!!nominaEspecialDialog}
        busy={batchBusy}
        count={selected.size}
        onClose={() => setNominaEspecialDialog(null)}
        onCrear={ejecutarCrearNominaEspecial}
      />

      {/* Dialog mover factura individual */}
      <MoverFacturaDialog
        open={!!moverDialog}
        factura={moverDialog?.factura}
        nominas={moverDialog?.nominas || []}
        proximosViernes={proximosViernes}
        fechaCustom={moverFechaCustom}
        setFechaCustom={setMoverFechaCustom}
        onClose={() => setMoverDialog(null)}
        onMover={doMover}
      />

      <VBForzadoDialog
        key={vbDialog?.factura?.id || 'vb-none'}
        open={!!vbDialog}
        factura={vbDialog?.factura}
        onClose={() => setVbDialog(null)}
        onConfirm={confirmVBForzado}
      />

      {/* Dialog limpiar residuos */}
      <CleanupResiduosDialog
        open={cleanupOpen}
        preview={cleanupPreview}
        busy={cleanupBusy}
        montoMax={cleanupMontoMax}
        setMontoMax={setCleanupMontoMax}
        onClose={() => {
          setCleanupOpen(false);
          setCleanupPreview(null);
        }}
        onReload={abrirCleanup}
        onConfirm={ejecutarCleanup}
      />

      {/* AlertDialog confirmar pago factura individual */}
      <ConfirmarPagoFacturaDialog
        open={!!confirmPay}
        factura={confirmPay?.factura}
        busy={payBusy}
        onClose={() => setConfirmPay(null)}
        onConfirm={ejecutarPago}
      />

      {/* AlertDialog confirmar pago nómina completa */}
      <ConfirmarPagoNominaDialog
        open={!!confirmPayNomina}
        nomina={confirmPayNomina?.nomina}
        busy={payNominaBusy}
        onClose={() => setConfirmPayNomina(null)}
        onConfirm={ejecutarPagoNomina}
      />

      {/* Iter 268: previsualización del archivo TEF (Descargar / Cancelar) */}
      <TefResultDialog
        result={tefResult}
        onCancel={() => setTefResult(null)}
        onDownload={descargarTefResult}
      />

      {/* Iter 268.f: tras descargar, confirmar registro del pago en Contabilidad (Alegra) */}
      <TefContabConfirmDialog
        open={!!tefContab}
        count={tefContab?.count}
        nombre={tefContab?.nombre}
        incluidas={tefContab?.incluidas}
        busy={tefContabBusy}
        onClose={() => setTefContab(null)}
        onConfirm={ejecutarTefContabilizar}
      />
    </div>
  );
}
